import { RetencaoMarcacao } from '../api/cccApi';
import { subsectionLabel } from '../config/retencaoWorkspace';
import type { ProposalServiceFile } from './proposalServiceDetails';
import { buildProposalServiceGroups, serviceDisplayName } from './proposalServiceDetails';
import { buildComercialGestaoNoteGroups } from './gestaoMarcacaoNotes';
import {
  buildComercialRequestMeta,
  getAllProposalsFromMarcacao,
  getCccProposalsFromMarcacao,
  hasCccPedidoProposta,
  isCccOriginatedProposal,
  type ComercialProposalRequestMeta,
  type RequestCancellationInfo,
  type ServiceCompletionDisplay,
  type RequestStatusBadge,
} from './proposalRequestsDisplay';

/** Comercial Gestão substage labels (tenant comercial, before CCC). */
const COMERCIAL_GESTAO_LABELS: Record<string, string> = {
  'por-contactar-iniciado-atribuido': 'Iniciado/Atribuído',
  'por-contactar-nao-atendido': 'Não Atendido',
  'por-contactar-incontactavel': 'Incontactável',
  'em-tratamento-aguardar-fatura': 'A aguardar Fatura',
  'em-tratamento-aguardar-proposta-bo': 'A aguardar Proposta BO',
  'em-tratamento-sem-interesse': 'Sem Interesse',
  'em-tratamento-pendente': 'Pendente',
  'em-tratamento-fidelizados': 'Fidelizados',
  'pre-fecho-proposta-enviada': 'Proposta Enviada',
  'pre-fecho-assinado': 'Assinatura',
  'pre-fecho-fidelizado-f': 'Fidelizado (F)',
  'pre-fecho-perdido-sem-assinatura': 'Perdido',
  'fecho-pendente-documentos': 'Pendente Documentos',
  'fecho-contrato-submetido': 'Contrato Submetido',
  'agendamentos-agendamentos': 'Agendamentos',
  'agendamentos-remarcacao': 'Remarcação',
  'reuniao-realizada-reuniao': 'Reunião Realizada',
  'feedback-pendencia-solicitar-proposta': 'Solicitar Proposta',
  'feedback-pendencia-concluido-sem-proposta': 'Concluído Sem Proposta',
  'feedback-pendencia-concluido-com-proposta': 'Concluído Com Proposta',
  'feedback-pendencia-not-ok-cliente': 'Not Ok Cliente',
  'feedback-pendencia-not-ok-callcenter': 'Not Ok CallCenter',
  'feedback-pendencia-pendente': 'Pendente',
  'resultado-fecho-ok': 'Fecho Ok',
  'resultado-fecho-not-ok': 'Fecho Not Ok',
  'resultado-aguardar': 'Aguardar',
  'resultado-cancelado': 'Cancelado',
};

const ESTADO_PROPOSTA_LABELS: Record<string, string> = {
  pendente: 'Pedido Submetido',
  available: 'Por Iniciar',
  em_progresso: 'Em Processo',
  in_progress: 'Em Processo',
  concluido: 'Proposta disponível',
  entregue: 'Proposta disponível',
  completed: 'Proposta disponível',
  cancelado: 'Cancelado',
  cancelled: 'Cancelado',
  roadtrip: 'RoadTrip',
  op_criada: 'OP Criada',
  proposta_aprovacao: 'Proposta em Aprovação',
  exclusividade_pendente: 'Exclusividade',
  pendente_gestor_edp: 'Pendente Gestor EDP (Erro Sistema)',
  pendente_comercial: 'Pendente Comercial',
};

const comercialGestaoLabel = (substage?: string) =>
  COMERCIAL_GESTAO_LABELS[substage || ''] ||
  (substage || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatEstadoPropostaValue = (raw: unknown): string => {
  if (raw == null) return '';
  const text = String(raw).trim();
  if (!text || text === '-') return '';
  const key = text.toLowerCase();
  return ESTADO_PROPOSTA_LABELS[key] || text;
};

/** Estado left on Comercial tenant before CCC Retenção. */
export const getEstadoAnteriorProposta = (m: RetencaoMarcacao): string | null => {
  const fromApi = m.retencao_claim?.estado_anterior_proposta?.trim();
  if (fromApi) return fromApi;

  const snap = m.retencao_claim?.comercial_snapshot;
  if (snap) {
    const display = snap.estado_anterior_display?.trim();
    if (display) return display;
    const ep = snap.estado_proposta_label?.trim();
    if (ep) return ep;
    const gs = snap.gestao_substage_label?.trim();
    if (gs) return gs;
  }

  const fd = (m.form_data || {}) as Record<string, unknown>;
  const estado = formatEstadoPropostaValue(fd.estadoProposta ?? fd.estado_proposta);
  if (estado) return estado;
  const gestao = comercialGestaoLabel(m.gestao_substage);
  return gestao || null;
};

export type LeadDetailRow = {
  key: string;
  label: string;
  value: string;
  href?: string;
};

export type LeadDetailGroup = {
  id: string;
  title: string;
  rows: LeadDetailRow[];
  files?: ProposalServiceFile[];
  leadId?: number | null;
};

export type ComercialProposalRequestLayout = ComercialProposalRequestMeta & {
  empresa: LeadDetailGroup | null;
  servicos: LeadDetailGroup[];
};

export type LeadDetailsLayout = {
  empresa: LeadDetailGroup | null;
  servicos: LeadDetailGroup[];
  /** Tab Comercial: notas da Gestão das Marcações (Comercial) */
  gestaoNotes?: LeadDetailGroup[];
  /** Tab Comercial: um bloco por pedido (proposalData[]) */
  comercialRequests?: ComercialProposalRequestLayout[];
};

export type {
  RequestStatusBadge,
  ServiceCompletionDisplay,
  RequestCancellationInfo,
};

const PRODUTO_LABELS: Record<string, string> = {
  'bairro-solar': 'Bairro Solar',
  pap: 'PAP',
  gas: 'Gás',
  eletricidade: 'Eletricidade',
  transacional: 'Transacional',
  luz: 'Luz',
  mobilidade: 'Mobilidade',
  outro: 'Outro',
};

const SERVICE_DETAIL_LABELS: Record<string, string> = {
  moradaFornecimento: 'Morada de fornecimento',
  moradaInstalacao: 'Morada de instalação',
  cpe: 'CPE',
  potencia: 'Potência',
  potenciaOutra: 'Outra potência',
  potenciaContratada: 'Potência contratada',
  sistema: 'Sistema elétrico',
  formaPagamento: 'Forma de pagamento',
  cui: 'CUI',
  escalao: 'Escalão',
  dataFecho: 'Data de fecho',
  coordenadasGeograficas: 'Coordenadas geográficas',
  observacoes: 'Observações',
  plataformaGestao: 'Plataforma de gestão',
  localInstalacao: 'Local de instalação',
  quadroMobilidade: 'Quadro de mobilidade elétrica',
  tipoFixacao: 'Tipo de fixação',
  tipoCarregamento: 'Tipo de carregamento',
  distanciaQveProximo: 'Distância QVE/QE (mais próximo)',
  distanciaQveDistante: 'Distância QVE/QE (mais distante)',
  jaTemPaineis: 'Já tem painéis',
  potencia1035: 'Potência 10/35 kVA',
  potencia69: 'Potência 6,9 kVA',
  trifasico: 'Trifásico',
  tipoTelhado: 'Tipo de telhado',
  faturaPresenteBairro: 'Fatura presente (visita)',
  faturaPresentePAP: 'Fatura presente (visita)',
  faturaPresenteGas: 'Fatura presente (reunião)',
  faturaPresenteEletricidade: 'Fatura presente (reunião)',
  gasNatural: 'Gás natural canalizado',
  comercializadoraGas: 'Comercializadora atual',
  comercializadoraEletricidade: 'Comercializadora atual',
  comercializadoraPAP: 'Comercializadora atual',
  valorFaturaGas: 'Valor da última fatura',
  valorFaturaEletricidade: 'Valor da última fatura',
  pagaMais250: 'Paga mais de 250 €',
  cae: 'CAE',
  iban: 'IBAN',
};

const formatYesNo = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  const s = String(value).trim().toLowerCase();
  if (s === 'sim' || s === 'true' || s === '1') return 'Sim';
  if (s === 'nao' || s === 'não' || s === 'false' || s === '0') return 'Não';
  if (s === 'nao_sabe' || s === 'não sabe') return 'Não sabe';
  return String(value).trim();
};

const firstNonEmpty = (...values: (unknown)[]): string | null => {
  for (const v of values) {
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return null;
};

const formatCodigoPostal = (cp1?: string | null, cp2?: string | null): string | null => {
  const a = cp1?.trim();
  const b = cp2?.trim();
  if (a && b) return `${a}-${b}`;
  return a || b || null;
};

const humanizeKey = (key: string): string => {
  if (SERVICE_DETAIL_LABELS[key]) return SERVICE_DETAIL_LABELS[key];
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

const getCallcenterSnapshot = (fd: Record<string, unknown>): Record<string, unknown> => {
  const cc = fd.original_callcenter;
  if (cc && typeof cc === 'object' && !Array.isArray(cc)) {
    return cc as Record<string, unknown>;
  }
  return fd;
};

const pickCc = (
  cc: Record<string, unknown>,
  fd: Record<string, unknown>,
  m: RetencaoMarcacao,
  keys: { cc?: string; fd?: string; model?: keyof RetencaoMarcacao }[]
): string | null => {
  for (const k of keys) {
    const fromCc = k.cc ? cc[k.cc] : undefined;
    const v = firstNonEmpty(fromCc, k.fd ? fd[k.fd] : undefined, k.model ? m[k.model] : undefined);
    if (v) return v;
  }
  return null;
};

const formatLocalidadePostal = (
  cc: Record<string, unknown>,
  fd: Record<string, unknown>,
  m: RetencaoMarcacao
): string | null => {
  const cp1 = pickCc(cc, fd, m, [
    { cc: 'codigoPostal1', fd: 'codigoPostal1', model: 'codigo_postal1' },
  ]);
  const cp2 = pickCc(cc, fd, m, [
    { cc: 'codigoPostal2', fd: 'codigoPostal2', model: 'codigo_postal2' },
  ]);
  const localidade = firstNonEmpty(cc.localidade, fd.localidade, fd.distrito, fd.regiao);
  const cp = formatCodigoPostal(cp1, cp2);
  if (localidade && cp) return `${localidade} · ${cp}`;
  if (localidade) return localidade;
  return cp;
};

const getCallcenterNota = (
  cc: Record<string, unknown>,
  fd: Record<string, unknown>,
  m: RetencaoMarcacao,
  hasOriginalSnapshot: boolean
): string | null => {
  const fromCc = firstNonEmpty(cc.marcacaoDescription);
  if (fromCc) return fromCc;
  if (hasOriginalSnapshot) return null;
  return firstNonEmpty(m.marcacao_description, fd.marcacaoDescription);
};

export const produtoLabel = (p?: string) => (p && PRODUTO_LABELS[p]) || p || '—';

export const formatMarcacaoDate = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-PT');
  } catch {
    return iso;
  }
};

export const formatMarcacaoSchedule = (m: RetencaoMarcacao) => {
  const d = formatMarcacaoDate(m.date);
  const start = m.hora_inicio?.trim();
  const end = m.hora_fim?.trim();
  if (start && end) return `${d}, ${start} – ${end}`;
  if (start) return `${d}, ${start}`;
  return d;
};

export const getMarcacaoNif = (m: RetencaoMarcacao) => {
  const fd = (m.form_data || {}) as Record<string, unknown>;
  const v = fd.nifEmpresa ?? fd.nif;
  return v != null && String(v).trim() ? String(v).trim() : null;
};

export const getMarcacaoContacto = (m: RetencaoMarcacao) => {
  const fd = (m.form_data || {}) as Record<string, unknown>;
  const candidates = [m.contacto_cliente, fd.contactoResponsavel, fd.contactoRepresentante, fd.telefone];
  for (const c of candidates) {
    if (c != null && String(c).trim()) return String(c).trim();
  }
  return null;
};

const row = (key: string, label: string, value: unknown, href?: string): LeadDetailRow | null => {
  const text =
    href != null
      ? 'Abrir no Google Maps'
      : typeof value === 'boolean'
        ? formatYesNo(value)
        : value != null
          ? String(value).trim()
          : '';
  if (!text) return null;
  return { key, label, value: text, href };
};

const BAIRRO_SOLAR_KEYS = [
  'jaTemPaineis',
  'potencia1035',
  'trifasico',
  'tipoTelhado',
  'faturaPresenteBairro',
] as const;

const PAP_KEYS = [
  'jaTemPaineisPAP',
  'potencia69',
  'trifasicoPAP',
  'trifasico',
  'tipoTelhadoPAP',
  'comercializadoraPAP',
  'pagaMais250',
  'faturaPresentePAP',
] as const;

const GAS_KEYS = ['gasNatural', 'comercializadoraGas', 'valorFaturaGas', 'faturaPresenteGas'] as const;

const ELETRICIDADE_KEYS = [
  'comercializadoraEletricidade',
  'valorFaturaEletricidade',
  'faturaPresenteEletricidade',
] as const;

function productFieldRows(
  service: string,
  sources: Record<string, unknown>[]
): LeadDetailRow[] {
  const norm = service.toLowerCase().replace(/\s+/g, '-');
  let keys: readonly string[] = [];
  if (norm === 'bairro-solar') keys = BAIRRO_SOLAR_KEYS;
  else if (norm === 'pap') keys = PAP_KEYS;
  else if (norm === 'gas' || norm === 'gás') keys = GAS_KEYS;
  else if (norm === 'eletricidade') keys = ELETRICIDADE_KEYS;

  const rows: LeadDetailRow[] = [];
  const seen = new Set<string>();

  keys.forEach((key) => {
    if (seen.has(key)) return;
    let raw: unknown;
    for (const src of sources) {
      if (src[key] !== undefined && src[key] !== null && src[key] !== '') {
        raw = src[key];
        break;
      }
    }
    if (raw === undefined) return;
    const value = formatYesNo(raw) ?? String(raw).trim();
    if (!value) return;
    seen.add(key);
    rows.push({ key, label: humanizeKey(key), value });
  });

  return rows;
}

function buildEmpresaAgendamentoGroup(
  m: RetencaoMarcacao,
  fd: Record<string, unknown>
): LeadDetailGroup | null {
  const hasOriginalSnapshot = !!(
    fd.original_callcenter &&
    typeof fd.original_callcenter === 'object' &&
    !Array.isArray(fd.original_callcenter)
  );
  const cc = getCallcenterSnapshot(fd);

  const rows: LeadDetailRow[] = [];
  const push = (r: LeadDetailRow | null) => {
    if (r) rows.push(r);
  };

  push(
    row(
      'nomeEmpresa',
      'Nome da empresa',
      pickCc(cc, fd, m, [
        { cc: 'nomeEmpresa', fd: 'nomeEmpresa', model: 'nome_empresa' },
      ])
    )
  );
  push(
    row(
      'nomeResponsavel',
      'Nome do responsável',
      pickCc(cc, fd, m, [
        { cc: 'nomeResponsavel', fd: 'nomeResponsavel', model: 'nome_responsavel' },
      ])
    )
  );
  push(
    row(
      'contacto',
      'Contacto',
      pickCc(cc, fd, m, [
        { cc: 'contactoCliente', fd: 'contactoCliente', model: 'contacto_cliente' },
        { fd: 'contactoResponsavel' },
        { fd: 'telefone' },
      ])
    )
  );
  push(
    row(
      'morada',
      'Morada',
      pickCc(cc, fd, m, [
        { cc: 'morada', fd: 'morada', model: 'morada' },
        { fd: 'moradaFornecimento' },
        { fd: 'moradaInstalacao' },
      ])
    )
  );
  push(row('localidadeCp', 'Localidade / código postal', formatLocalidadePostal(cc, fd, m)));

  const maps = pickCc(cc, fd, m, [
    { cc: 'linkGoogleMaps', fd: 'linkGoogleMaps', model: 'link_google_maps' },
  ]);
  if (maps && /^https?:\/\//i.test(maps)) {
    push(row('maps', 'Link do Google Maps', maps, maps));
  }

  const nota = getCallcenterNota(cc, fd, m, hasOriginalSnapshot);
  push(row('notaCallCenter', 'Nota CallCenter', nota));

  const estadoAnterior = getEstadoAnteriorProposta(m);
  push(row('estadoAnteriorProposta', 'Estado anterior da proposta', estadoAnterior));

  if (!rows.length) return null;
  return { id: 'empresa-agendamento', title: 'Informações da Empresa (Agendamento)', rows };
}

function mergeExtraVisitRows(
  service: string,
  rows: LeadDetailRow[],
  sources: Record<string, unknown>[]
): LeadDetailRow[] {
  const productRows = productFieldRows(service, sources);
  const merged = [...rows];
  productRows.forEach((pr) => {
    if (!merged.some((r) => r.key === pr.key)) merged.push(pr);
  });
  return merged;
}

function buildCallcenterServiceGroups(m: RetencaoMarcacao, fd: Record<string, unknown>): LeadDetailGroup[] {
  const cc = getCallcenterSnapshot(fd);
  const mergedFd: Record<string, unknown> = {
    ...cc,
    servicos: fd.servicos ?? cc.servicos,
    serviceDetails: { ...(cc.serviceDetails as object), ...(fd.serviceDetails as object) },
    fileReferences: fd.fileReferences ?? cc.fileReferences,
    empresa: fd.empresa ?? cc.empresa,
    equipaInterna: fd.equipaInterna ?? cc.equipaInterna,
    lead_id: fd.lead_id ?? cc.lead_id ?? fd.leadId ?? cc.leadId,
  };

  const fromProposalShape = buildProposalServiceGroups(mergedFd);
  const servicosList = Array.isArray(mergedFd.servicos) ? mergedFd.servicos : [];
  if (fromProposalShape.length > 0) {
    return fromProposalShape.map((g, idx) => {
      const inst = servicosList[idx];
      const item =
        inst && typeof inst === 'object' && !Array.isArray(inst)
          ? (inst as Record<string, unknown>)
          : {};
      const service = String(item.service || item.produto || m.produto || 'servico');
      const instanceId = String(item.instanceId || `svc-${idx}`);
      const serviceDetails = (mergedFd.serviceDetails || {}) as Record<
        string,
        Record<string, unknown>
      >;
      const details = serviceDetails[instanceId] || {};
      const sources = [details, fd, cc, (m.product_details || {}) as Record<string, unknown>];
      return {
        ...g,
        rows: mergeExtraVisitRows(service, g.rows, sources),
      };
    });
  }

  const produtosRaw = fd.produtos ?? cc.produtos ?? m.produto;
  const produtos = Array.isArray(produtosRaw)
    ? produtosRaw.map(String)
    : produtosRaw
      ? [String(produtosRaw)]
      : [];
  if (produtos.length === 0 && m.produto) produtos.push(m.produto);

  return produtos
    .map((prod, idx) => {
      const sources = [fd, cc, (m.product_details || {}) as Record<string, unknown>];
      const rows = productFieldRows(prod, sources);
      return {
        id: `produto-${prod}-${idx}`,
        title: serviceDisplayName(prod) || produtoLabel(prod),
        rows,
        files: [],
        leadId: null,
      };
    })
    .filter((g) => g.rows.length > 0);
}

export function getLatestProposalFromMarcacao(
  fd: Record<string, unknown> | undefined
): Record<string, unknown> | null {
  const list = getAllProposalsFromMarcacao(fd);
  return list.length > 0 ? list[0] : null;
}

export function hasComercialProposal(m: RetencaoMarcacao): boolean {
  return hasComercialTabProposals(m);
}

function buildComercialRequestLayout(
  m: RetencaoMarcacao,
  fd: Record<string, unknown>,
  proposal: Record<string, unknown>,
  index: number
): ComercialProposalRequestLayout {
  const meta = buildComercialRequestMeta(fd, proposal, index);
  const proposalFd: Record<string, unknown> = {
    servicos: proposal.servicos,
    serviceDetails: proposal.serviceDetails,
    fileReferences: proposal.fileReferences,
    empresa: proposal.empresa,
    equipaInterna: proposal.equipaInterna ?? fd.equipaInterna,
    lead_id: proposal.lead_id ?? proposal.leadId ?? fd.lead_id ?? fd.leadId,
  };
  return {
    ...meta,
    empresa: buildEmpresaPropostaGroup(m, fd, proposal),
    servicos: buildProposalServiceGroups(proposalFd, proposal),
  };
}

export function buildEmpresaPropostaGroup(
  m: RetencaoMarcacao,
  fd: Record<string, unknown>,
  proposal: Record<string, unknown>
): LeadDetailGroup | null {
  const empresa = (proposal.empresa || {}) as Record<string, unknown>;
  const ic = (fd.informacoesComerciais || {}) as Record<string, unknown>;
  const rows: LeadDetailRow[] = [];
  const push = (r: LeadDetailRow | null) => {
    if (r) rows.push(r);
  };

  const propostaId = firstNonEmpty(proposal.propostaId, proposal.proposta_id);
  if (propostaId) push(row('propostaId', 'ID da proposta', propostaId));

  push(row('nomeEmpresa', 'Nome da empresa', empresa.nomeEmpresa ?? m.nome_empresa));
  push(row('nomeResponsavel', 'Nome do responsável', empresa.nomeRepresentante ?? m.nome_responsavel));
  push(
    row(
      'contacto',
      'Contacto',
      firstNonEmpty(
        empresa.contactoRepresentante,
        empresa.contactoResponsavel,
        m.contacto_cliente
      )
    )
  );
  push(
    row(
      'nif',
      'NIF',
      firstNonEmpty(empresa.nifEmpresa, fd.nifEmpresa, fd.nif)
    )
  );
  push(
    row(
      'morada',
      'Morada',
      firstNonEmpty(empresa.moradaFornecimento, empresa.moradaCorrespondencia, empresa.morada, m.morada)
    )
  );
  const localidade = firstNonEmpty(empresa.regiao, empresa.localidade, fd.localidade);
  const cp = formatCodigoPostal(
    String(empresa.codigoPostal1 || fd.codigoPostal1 || m.codigo_postal1 || ''),
    String(empresa.codigoPostal2 || fd.codigoPostal2 || m.codigo_postal2 || '')
  );
  if (localidade || cp) {
    push(
      row(
        'localidadeCp',
        'Localidade / código postal',
        localidade && cp ? `${localidade} · ${cp}` : localidade || cp
      )
    );
  }
  push(row('emailEmpresa', 'Email da empresa', empresa.emailEmpresa));
  push(row('emailRepresentante', 'Email do representante', empresa.emailRepresentante));

  const maps = firstNonEmpty(empresa.linkGoogleMaps, fd.linkGoogleMaps, m.link_google_maps);
  if (maps && /^https?:\/\//i.test(maps)) {
    push(row('maps', 'Link do Google Maps', maps, maps));
  }

  const notaIc = ic.nota != null ? String(ic.nota).trim() : '';
  if (notaIc) push(row('notaComercial', 'Nota comercial', notaIc));

  const submittedAt = firstNonEmpty(proposal.submittedAt, proposal.submitted_at);
  if (submittedAt) {
    try {
      const d = new Date(submittedAt);
      if (!Number.isNaN(d.getTime())) {
        push(row('submittedAt', 'Proposta solicitada em', d.toLocaleString('pt-PT')));
      }
    } catch {
        push(row('submittedAt', 'Proposta solicitada em', submittedAt));
    }
  }

  if (!rows.length) return null;
  return {
    id: 'empresa-proposta',
    title: 'Informações da Empresa (Proposta Comercial)',
    rows,
  };
}

/** CallCenter agendamento snapshot */
export function buildCallcenterLeadDetailsLayout(m: RetencaoMarcacao): LeadDetailsLayout {
  const fd = (m.form_data || {}) as Record<string, unknown>;
  return {
    empresa: buildEmpresaAgendamentoGroup(m, fd),
    servicos: buildCallcenterServiceGroups(m, fd),
  };
}

/** Pedidos Comercial (exclui pedidos originados no CCC). */
export function buildComercialLeadDetailsLayout(m: RetencaoMarcacao): LeadDetailsLayout {
  const fd = (m.form_data || {}) as Record<string, unknown>;
  const gestaoNotes = buildComercialGestaoNoteGroups(m);
  const comercialOnly = getAllProposalsFromMarcacao(fd).filter((p) => !isCccOriginatedProposal(p));
  if (!comercialOnly.length) {
    return { empresa: null, servicos: [], gestaoNotes, comercialRequests: [] };
  }
  return {
    empresa: null,
    servicos: [],
    gestaoNotes,
    comercialRequests: comercialOnly.map((proposal, index) =>
      buildComercialRequestLayout(m, fd, proposal, index)
    ),
  };
}

export function hasComercialTabProposals(m: RetencaoMarcacao): boolean {
  const fd = (m.form_data || {}) as Record<string, unknown>;
  return getAllProposalsFromMarcacao(fd).some((p) => !isCccOriginatedProposal(p));
}

/** Tab Pedido de Proposta — apenas pedidos submetidos pelo CCC. */
export function buildPedidoPropostaLeadDetailsLayout(m: RetencaoMarcacao): LeadDetailsLayout {
  const fd = (m.form_data || {}) as Record<string, unknown>;
  const proposals = getCccProposalsFromMarcacao(fd);
  if (!proposals.length) {
    return { empresa: null, servicos: [], comercialRequests: [] };
  }
  return {
    empresa: null,
    servicos: [],
    comercialRequests: proposals.map((proposal, index) =>
      buildComercialRequestLayout(m, fd, proposal, index)
    ),
  };
}


/** @deprecated Use buildCallcenterLeadDetailsLayout */
export function buildMarcacaoLeadDetailsLayout(m: RetencaoMarcacao): LeadDetailsLayout {
  return buildCallcenterLeadDetailsLayout(m);
}

/** Substage for CCC área de trabalho (claimed rows). Does not use Comercial DB gestao_substage. */
export function getWorkspaceSubstage(m: RetencaoMarcacao): string {
  const fromClaim = m.retencao_claim?.ccc_substage;
  if (typeof fromClaim === 'string' && fromClaim.trim()) return fromClaim.trim();
  if (typeof m.ccc_substage === 'string' && m.ccc_substage.trim()) return m.ccc_substage.trim();
  if (m.is_mine) {
    return m.gestao_substage || 'por-contactar-iniciado-atribuido';
  }
  return m.comercial_gestao_substage || m.gestao_substage || 'agendamentos-agendamentos';
}

/** Email do representante for Envio da Proposta autofill. */
export function getRepresentanteEmailFromMarcacao(m: RetencaoMarcacao): string {
  const fd = (m.form_data || {}) as Record<string, unknown>;
  const cc = getCallcenterSnapshot(fd);
  const ccEmpresa = (cc.empresa || {}) as Record<string, unknown>;
  const fdEmpresa = (fd.empresa || {}) as Record<string, unknown>;
  const proposals = getAllProposalsFromMarcacao(fd);
  const latestProposal = proposals.length > 0 ? proposals[0] : null;
  const propEmpresa = latestProposal
    ? ((latestProposal.empresa || {}) as Record<string, unknown>)
    : {};
  return (
    firstNonEmpty(
      fd.emailRepresentante,
      ccEmpresa.emailRepresentante,
      fdEmpresa.emailRepresentante,
      propEmpresa.emailRepresentante,
      fd.emailRepresentanteLegal
    ) || ''
  );
}

export const getMarcacaoRowSummary = (m: RetencaoMarcacao) => {
  const isClaimed = m.retencao_claim?.claimed_by != null;
  const substage = isClaimed ? getWorkspaceSubstage(m) : m.comercial_gestao_substage || m.gestao_substage || 'agendamentos-agendamentos';
  const fd = (m.form_data || {}) as Record<string, unknown>;
  const email = firstNonEmpty(fd.emailEmpresa, fd.emailRepresentante, fd.emailRepresentanteLegal);
  const cc = getCallcenterSnapshot(fd);
  const morada = pickCc(cc, fd, m, [{ cc: 'morada', fd: 'morada', model: 'morada' }, { fd: 'moradaFornecimento' }]);
  return {
    empresa: m.nome_empresa || '—',
    responsavel: m.nome_responsavel || '—',
    nif: getMarcacaoNif(m) || '—',
    produto: produtoLabel(m.produto),
    schedule: formatMarcacaoSchedule(m),
    contacto: getMarcacaoContacto(m) || '—',
    email,
    morada,
    estado: isClaimed ? subsectionLabel(substage) : comercialGestaoLabel(substage),
    substage,
  };
};
